// filepath: apps/web/src/features/proc/useStepLog.ts
//
// FR-UI-PROC-1 AC-1 — TanStack Query hook for the execution-trace
// viewer (per-instance step log).
//
// Issues GET /api/processes/instances/{id}/step-log and returns the
// full step log for the instance. The cache key includes tenantSlug
// as the 2nd segment (INV-1) and instanceId as the 4th so concurrent
// views of two instances do not collide.
//
// Wire shape: top-level StepLogEntry[] (internal/obs/handler.go:104-167).
// The endpoint is tenant-scoped server-side; the SPA trusts the
// wire contract.
//
// FR-UI-NFR-2: the SPA treats every entry as DATA ONLY. No parsing
// of payload semantics, no client-side execution, no
// dangerouslySetInnerHTML. The renderer simply renders one row per
// entry (see ExecutionTraceViewer.tsx).

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "@/definitions/types";
import { useTenant } from "@/tenant/TenantProvider";
import { apiFetch } from "@/api";
import { procKeys, type StepLogEntry, type StepLogListResponse } from "./types";

export interface UseStepLogResult {
  data: StepLogEntry[] | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: ApiError | null;
  refetch: () => Promise<unknown>;
}

/**
 * useStepLog — TanStack Query hook backing the execution-trace
 * viewer.
 *
 * @param opts.instanceId — Process instance UUID. When empty, the
 *   query is disabled (no fetch — the page cannot render a trace
 *   for an unknown instance).
 */
export function useStepLog(
  opts: { instanceId: string },
  apiBase = "/api",
): UseStepLogResult {
  const { tenantSlug } = useTenant();
  const instanceId = opts.instanceId;
  const enabled = instanceId.length > 0;

  const query: UseQueryResult<StepLogEntry[], ApiError> = useQuery<
    StepLogEntry[],
    ApiError
  >({
    queryKey: procKeys.stepLog(tenantSlug, instanceId),
    queryFn: async ({ signal }) => {
      const url = `${apiBase}/processes/instances/${encodeURIComponent(
        instanceId,
      )}/step-log`;

      let response: Response;
      try {
        // ISS-377 round 3 R2-B2.5: migrated to apiFetch. The
        // GET request carries an AbortSignal via `init.signal`
        // so TanStack Query's unmount-cancellation reaches the
        // underlying fetch; apiFetch's signal-pass-through is
        // byte-identical to the previous shape.
        response = await apiFetch(url, {
          method: "GET",
          init: {
            headers: { Accept: "application/json" },
            signal,
          },
        });
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }

      if (!response.ok) {
        const body = await safeReadJson(response);
        const code =
          (body as { code?: string } | null)?.code ?? `http_${response.status}`;
        const retryAfter = parseRetryAfter(response.headers.get("Retry-After"));
        throw new ApiError(
          (body as { error?: string } | null)?.error ?? response.statusText,
          response.status,
          code,
          retryAfter,
        );
      }

      const wire = (await response.json()) as StepLogListResponse;
      return normalise(wire);
    },
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

function normalise(wire: StepLogListResponse): StepLogEntry[] {
  if (!Array.isArray(wire)) return [];
  // Sort defensively by `started_at ASC`. The server contract is
  // already sorted, but a defensive in-place sort guarantees the
  // UI never displays steps out of order if the server's contract
  // ever drifts.
  return [...wire].sort((a, b) => {
    const aStart = Date.parse(a.started_at);
    const bStart = Date.parse(b.started_at);
    if (!Number.isFinite(aStart) || !Number.isFinite(bStart)) return 0;
    return aStart - bStart;
  });
}

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

function parseRetryAfter(header: string | null): number | null {
  if (header === null) return null;
  const trimmed = header.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (Number.isFinite(n) && n >= 0) return n;
  // Retry-After may be an HTTP-date; treat non-numeric as unknown.
  return null;
}
